import { useContext, useMemo } from 'react';
import { FormProvider, useWatch } from 'react-hook-form';

import { Accordion } from '@radix-ui/react-accordion';
import {
  BoxIcon,
  Building2,
  CircleUserRound,
  FileText,
  Package,
  PackagePlus,
  ReceiptIcon,
  TruckIcon,
  WarehouseIcon,
  Weight,
} from 'lucide-react';

import { Button } from 'components/Button';
import { TooltipProvider } from 'components/Tooltip';
import { Flex } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import { ExtendedFormProvider } from 'contexts/extendedFormContext';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { TMSContext } from 'contexts/tms';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { LoadSectionAccordionItem } from 'pages/LoadView/LoadInformation/Components';
import { ThreeGTwoStopFields } from 'pages/LoadView/LoadInformation/Components/ThreeG/ThreeG';
import { CarrierSectionForm } from 'pages/LoadView/LoadInformation/Sections/CarrierSectionForm';
import LoadBuildingAccordionControls from 'pages/QuoteView/LoadBuilding/components/LoadBuildingAccordionControls';
import { LoadCreatedMessage } from 'pages/QuoteView/LoadBuilding/components/LoadCreatedMessage';
import { LoadStopTypes, NormalizedLoad } from 'types/Load';
import { Undef } from 'types/UtilityTypes';
import ButtonText from 'types/enums/ButtonText';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { getThreeGInternalIdFromLoadNotes } from 'utils/getThreeGInternalIdFromLoadNotes';
import { getThreeGOrderURLInTMS } from 'utils/threeGTMSUrls';

import DuplicateLoadWarningDialog from '../DuplicateLoadWarningDialog';
import { AccessorialsForm } from './Accessorials';
import { BillToSectionForm } from './BillTo';
// Extracted Section Forms
import { CustomerSectionForm } from './Customer';
import { DocumentsSectionForm } from './Documents';
import { LoadItemsSection } from './LoadItemsSection';
import { OperatorSectionForm } from './Operator';
import { SpecificationsForm } from './Specifications';
import { StopForm } from './Stop';
import { AvailableTabs } from './constants';
// The "Masterclass" Logic Hook
import { useThreeGFormLogic } from './hooks/useThreeGFormLogic';

interface UnifiedThreeGLoadFormProps {
  isCreateMode: boolean;
  load?: any;
  onSubmit?: (data: NormalizedLoad) => void;
  externalLinks?: any; // External links for edit mode
}

export default function UnifiedThreeGLoadForm({
  isCreateMode,
  load,
  onSubmit: _onSubmit,
  externalLinks: _externalLinks,
}: UnifiedThreeGLoadFormProps) {
  const { tmsIntegrations, serviceFeaturesEnabled } = useServiceFeatures();
  const tmsID = tmsIntegrations?.[0]?.id;

  const { currentState } = useContext(SidebarStateContext);
  const { openExternalUrl } = currentState;
  const { tenant } = useContext(TMSContext);

  const {
    formMethods,
    state: {
      activeTabs,
      setActiveTabs,
      expandAll,
      collapseAll,
      defaultView,
      setDefaultView,
      allTabs,
      isLoading,
      customers,
      locations,
      transportTypes,
      serviceOptions,
      ltlAccessorials,
      refNumRequirements,
      showAIHints,
      showDuplicateWarning,
      builtLoad,
      customerID,
      customerExternalTmsId,
      enableAutoFreightClass,
    },
    actions: {
      handleFormSubmit,
      handleConfirmedSubmit,
      handleClearForm,
      refreshLocations,
      refreshTransportTypes,
      refreshServiceOptions,
      setShowDuplicateWarning,
      setLocations,
      setCustomers,
      setCarrierIsQualified,
    },
  } = useThreeGFormLogic({
    isCreateMode,
    load,
    tmsID,
    threadId: currentState.threadId,
    emailId: currentState.emailId,
    threadItemId: currentState.threadItemId,
    clickedSuggestion: currentState.clickedSuggestion,
    originalSuggestionData: currentState.clickedSuggestion
      ?.suggested as Undef<NormalizedLoad>,
    onSubmit: _onSubmit,
  });

  const threeGOrderURLInTMS = useMemo(() => {
    if (!builtLoad) {
      return null;
    }

    const orderInternalId = getThreeGInternalIdFromLoadNotes(builtLoad.notes);
    if (!orderInternalId) {
      return null;
    }

    return getThreeGOrderURLInTMS(orderInternalId, tenant);
  }, [builtLoad, tenant]);

  const watchedMode = useWatch({
    control: formMethods.control,
    name: 'mode',
  });
  const isLTLMode = watchedMode === 'LTL';
  const submitButtonLabel = isCreateMode
    ? ButtonText.BuildLoad
    : ButtonText.UpdateTMS;

  return (
    <div className='mb-5'>
      <TooltipProvider>
        <ExtendedFormProvider
          aiDefaultValues={showAIHints}
          aiIconOnly={true}
          highlightDirtyFields={!isCreateMode}
        >
          <FormProvider {...formMethods}>
            <form onSubmit={handleFormSubmit}>
              <LoadBuildingAccordionControls
                activeTabs={activeTabs}
                allTabs={allTabs}
                onExpandAll={expandAll}
                onCollapseAll={collapseAll}
                defaultView={defaultView}
                onDefaultViewChange={setDefaultView}
              />
              <Accordion
                type='multiple'
                value={activeTabs}
                onValueChange={setActiveTabs}
              >
                <LoadSectionAccordionItem
                  label='Customer'
                  icon={<Building2 className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.customer}
                  activeTabs={activeTabs}
                >
                  <CustomerSectionForm
                    formMethods={formMethods}
                    customers={customers}
                    setCustomers={setCustomers}
                    isLoadingCustomers={isLoading.customers}
                    setIsLoadingCustomers={() => {}}
                    refNumRequirements={refNumRequirements}
                    originalSuggestionData={
                      currentState.clickedSuggestion
                        ?.suggested as Undef<NormalizedLoad>
                    }
                    suggestedCustomerExternalTMSID={
                      (
                        currentState.clickedSuggestion
                          ?.suggested as NormalizedLoad
                      )?.customer?.externalTMSID
                    }
                  />
                </LoadSectionAccordionItem>

                {!isCreateMode && (
                  <LoadSectionAccordionItem
                    label='Bill To'
                    icon={<ReceiptIcon className='h-6 w-6' strokeWidth={1} />}
                    name={AvailableTabs.billTo}
                    activeTabs={activeTabs}
                  >
                    <BillToSectionForm />
                  </LoadSectionAccordionItem>
                )}

                <LoadSectionAccordionItem
                  label='Specs'
                  icon={<Weight className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.specifications}
                  activeTabs={activeTabs}
                >
                  <SpecificationsForm
                    formMethods={formMethods}
                    transportTypes={transportTypes}
                    isLoadingTransportTypes={isLoading.transportTypes}
                    handleRefreshTransportTypes={refreshTransportTypes}
                    serviceOptions={serviceOptions}
                    isLoadingServiceOptions={isLoading.serviceOptions}
                    handleRefreshServiceOptions={refreshServiceOptions}
                    tmsID={tmsID}
                    isCreateMode={isCreateMode}
                  />
                </LoadSectionAccordionItem>

                <LoadSectionAccordionItem
                  label='Items'
                  icon={<Package className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.items}
                  activeTabs={activeTabs}
                >
                  <LoadItemsSection
                    formMethods={formMethods}
                    tmsID={tmsID}
                    customerID={customerID}
                    customerExternalTmsId={customerExternalTmsId}
                    enableAutoFreightClass={enableAutoFreightClass}
                  />
                </LoadSectionAccordionItem>

                {isLTLMode && (
                  <LoadSectionAccordionItem
                    label='Accessorials'
                    icon={<PackagePlus className='h-6 w-6' strokeWidth={1} />}
                    name={AvailableTabs.accessorials}
                    activeTabs={activeTabs}
                  >
                    <AccessorialsForm
                      formMethods={formMethods}
                      accessorials={ltlAccessorials}
                      isLoading={isLoading.accessorials}
                    />
                  </LoadSectionAccordionItem>
                )}

                <LoadSectionAccordionItem
                  label='Pickup'
                  icon={<BoxIcon className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.pickup}
                  activeTabs={activeTabs}
                >
                  <StopForm
                    stop={LoadStopTypes.Pickup}
                    formMethods={formMethods}
                    isLoadingLocations={isLoading.locations}
                    locations={locations}
                    handleRefreshLocations={refreshLocations}
                    originalSuggestionData={
                      currentState.clickedSuggestion
                        ?.suggested as Undef<NormalizedLoad>
                    }
                    tmsID={tmsID}
                    setLocations={setLocations}
                    isCreateMode={isCreateMode}
                    customerID={customerID}
                    customerExternalTmsId={customerExternalTmsId}
                    aiHighlightedExternalTMSID={
                      (
                        currentState.clickedSuggestion
                          ?.suggested as NormalizedLoad
                      )?.pickup?.externalTMSID
                    }
                  />
                  {!isCreateMode && (
                    <ThreeGTwoStopFields
                      isPickup={true}
                      currentLoad={formMethods.getValues()}
                    />
                  )}
                </LoadSectionAccordionItem>

                <LoadSectionAccordionItem
                  label='Consignee'
                  icon={<WarehouseIcon className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.consignee}
                  activeTabs={activeTabs}
                >
                  <StopForm
                    stop={LoadStopTypes.Consignee}
                    formMethods={formMethods}
                    isLoadingLocations={isLoading.locations}
                    locations={locations}
                    handleRefreshLocations={refreshLocations}
                    originalSuggestionData={
                      currentState.clickedSuggestion
                        ?.suggested as Undef<NormalizedLoad>
                    }
                    tmsID={tmsID}
                    setLocations={setLocations}
                    isCreateMode={isCreateMode}
                    customerID={customerID}
                    customerExternalTmsId={customerExternalTmsId}
                    aiHighlightedExternalTMSID={
                      (
                        currentState.clickedSuggestion
                          ?.suggested as NormalizedLoad
                      )?.consignee?.externalTMSID
                    }
                  />
                  {!isCreateMode && (
                    <ThreeGTwoStopFields
                      isPickup={false}
                      currentLoad={formMethods.getValues()}
                    />
                  )}
                </LoadSectionAccordionItem>

                {!isCreateMode && load && (
                  <LoadSectionAccordionItem
                    label='Carrier'
                    icon={<TruckIcon className='h-6 w-6' strokeWidth={1} />}
                    name={AvailableTabs.carrier}
                    activeTabs={activeTabs}
                  >
                    <CarrierSectionForm
                      suggestedFieldsPrevValues={undefined}
                      load={load}
                      formMethods={formMethods}
                      isLoadingCarriers={false} // 3G TMS doesn't fetch carriers - carrier data is manual entry only
                      carriers={null} // 3G TMS doesn't maintain carrier list
                      setIsLoadingCarriers={() => {}} // Not used for 3G TMS
                      setCarriers={() => {}} // Not used for 3G TMS
                      setIsCarrierQualified={setCarrierIsQualified}
                      hideCarrierTimeInputs={true}
                    />
                  </LoadSectionAccordionItem>
                )}

                {!isCreateMode && serviceFeaturesEnabled.isOperatorEnabled && (
                  <LoadSectionAccordionItem
                    label='Operator'
                    icon={
                      <CircleUserRound className='h-6 w-6' strokeWidth={1} />
                    }
                    name={AvailableTabs.operator}
                    activeTabs={activeTabs}
                  >
                    <OperatorSectionForm />
                  </LoadSectionAccordionItem>
                )}

                {!isCreateMode && (
                  <LoadSectionAccordionItem
                    label='Documents'
                    icon={<FileText className='h-6 w-6' strokeWidth={1} />}
                    name={AvailableTabs.documents}
                    activeTabs={activeTabs}
                  >
                    <DocumentsSectionForm loadId={load?.ID} />
                  </LoadSectionAccordionItem>
                )}
              </Accordion>

              <section className='w-full mt-4'>
                <Button
                  buttonNamePosthog={
                    isCreateMode
                      ? ButtonNamePosthog.BuildLoad
                      : ButtonNamePosthog.UpdateTMS
                  }
                  type='submit'
                  className='w-full'
                  disabled={
                    isLoading.submitting ||
                    isLoading.serviceOptions ||
                    isLoading.transportTypes
                  }
                  data-testid={
                    isCreateMode
                      ? 'create-load-submit'
                      : 'load-info-update-submit'
                  }
                >
                  {isLoading.submitting ? <ButtonLoader /> : submitButtonLabel}
                </Button>

                {isCreateMode && (
                  <>
                    {formMethods.formState.isDirty && (
                      <Flex justify='center' align='center'>
                        <Button
                          type='button'
                          className='w-50% mt-4 h-8 text-sm text-neutral-500'
                          variant='outline'
                          onClick={handleClearForm}
                          buttonNamePosthog={ButtonNamePosthog.ClearForm}
                        >
                          {ButtonText.ClearForm}
                        </Button>
                      </Flex>
                    )}
                    {builtLoad?.externalTMSID && (
                      <>
                        <LoadCreatedMessage
                          builtLoadId={builtLoad.externalTMSID}
                        />
                        {threeGOrderURLInTMS ? (
                          <Typography
                            variant='body-sm'
                            className='mb-1 cursor-pointer underline'
                            data-testid='created-load-link'
                            onClick={() => openExternalUrl(threeGOrderURLInTMS)}
                          >
                            Access the created load in 3G for more details
                          </Typography>
                        ) : null}
                      </>
                    )}
                  </>
                )}
              </section>
            </form>
          </FormProvider>
        </ExtendedFormProvider>
      </TooltipProvider>

      <DuplicateLoadWarningDialog
        open={showDuplicateWarning}
        onOpenChange={setShowDuplicateWarning}
        onConfirm={handleConfirmedSubmit}
        isLoading={isLoading.submitting}
      />
    </div>
  );
}
